import {Form, useSearchParams, useSubmit} from '@remix-run/react';
import {useRef, useCallback} from 'react';
import {Button} from '@cindedi/ui/components/Button';
import {SearchInput} from '@cindedi/ui/components/SearchInput';
import {SelectInput} from '@cindedi/ui/components/SelectInput';

export interface PublicationsFilterProps {
  streams: Array<{slug: string; name: string}>;
  /** Base path for the form action (e.g., /publicacoes/artigos) */
  basePath: string;
  /** Hide the type filter (when on a type-specific subpage) */
  hideTypeFilter?: boolean;
}

const DECADES = [
  {value: '2020', label: '2020s'},
  {value: '2010', label: '2010s'},
  {value: '2000', label: '2000s'},
  {value: '1990', label: '1990s'},
  {value: '1980', label: '1980s'},
  {value: '1970', label: '1970s'},
];

export function PublicationsFilter({streams, basePath, hideTypeFilter}: PublicationsFilterProps) {
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const submit = useSubmit();

  const currentQ = searchParams.get('q') ?? '';
  const currentDecade = searchParams.get('decade') ?? '';
  const currentStream = searchParams.get('stream') ?? '';
  const currentSort = searchParams.get('sort') ?? '';
  const currentType = searchParams.get('type') ?? '';

  const hasFilters = !!(currentQ || currentDecade || currentStream || currentType);

  const handleFilterChange = useCallback(() => {
    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (typeof value === 'string' && value.trim()) {
          params.set(key, value.trim());
        }
      }
      submit(params, {method: 'get', action: basePath});
    }
  }, [submit, basePath]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleFilterChange();
    },
    [handleFilterChange],
  );

  return (
    <Form ref={formRef} method="get" action={basePath} onSubmit={handleSubmit} className="flex flex-col gap-3 mb-6 mt-4" key={searchParams.toString()}>
      {/* Search */}
      <div className="flex gap-2">
        <SearchInput
          name="q"
          defaultValue={currentQ}
          placeholder="Buscar por título ou autor..."
        />
        <Button type="submit" variant="primary" size="sm">
          Buscar
        </Button>
      </div>

      {/* Filters row */}
      <div className="flex gap-2 flex-wrap items-center">
        {!hideTypeFilter && (
          <div className="w-44">
            <SelectInput name="type" defaultValue={currentType} onChange={handleFilterChange}>
              <option value="">Todos os tipos</option>
              <option value="article">Artigos</option>
              <option value="bookChapter">Capítulos de Livro</option>
              <option value="book">Livros</option>
            </SelectInput>
          </div>
        )}

        <div className="w-44">
          <SelectInput name="decade" defaultValue={currentDecade} onChange={handleFilterChange}>
            <option value="">Todas as décadas</option>
            {DECADES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </SelectInput>
        </div>

        {streams.length > 0 && (
          <div className="w-48">
            <SelectInput name="stream" defaultValue={currentStream} onChange={handleFilterChange}>
              <option value="">Todas as linhas</option>
              {streams.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </SelectInput>
          </div>
        )}

        <div className="w-40">
          <SelectInput name="sort" defaultValue={currentSort} onChange={handleFilterChange}>
            <option value="">Mais recentes</option>
            <option value="year:asc">Mais antigos</option>
            <option value="title:asc">Título A-Z</option>
            <option value="title:desc">Título Z-A</option>
          </SelectInput>
        </div>

        {hasFilters && (
          <a
            href={basePath}
            className="px-3 py-1.5 rounded text-sm text-shade-11 hover:text-shade-12 hover:bg-shade-3 transition-colors"
          >
            Limpar filtros
          </a>
        )}
      </div>
    </Form>
  );
}
